import { get, merge, forEach, isEmpty, set } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getVisibleItems } from 'resolvers/getVisibleEntities';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import shortid from 'shortid';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc MemberDetailsEligibilityWidget defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberDetailsEligibilityWidgetService } from 'services/memberDetailsEligibilityWidget';
 *
 * // create service instance and send/cancel request
 * const memberDetailsEligibilityWidgetService = new MemberDetailsEligibilityWidgetService();
 * const result = await memberDetailsEligibilityWidgetService.fetchData( '<user-id>' ); // fetch data
 * memberDetailsEligibilityWidgetService.cancel(); // cancel an active request
 */
export class MemberDetailsEligibilityWidgetService extends HttpService {

    // fetch call control widget data
    async fetchComponentData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                    }
                } ),
            } );

            // extract data
            const visibility = get( response, 'body.component.visibility', {} );
            const state = get( response, 'body.component.data.state', {} );
            const options = get( response, 'body.component.data.options', {} );
            const title = get( response, 'body.component.structure.title', {} );
            const basicInfo = get( response, 'body.component.structure.sections.basicInfo', {} );
            const memberInfo = get( state, 'memberInfo', [] );
            const otherCoverageInfo = get( state, 'otherCoverageInfo', {} );
            const otherCoverageInfoOptions = get( response, 'body.component.data.options.otherCoverage' );
            const actions = get( response, 'body.component.structure.actions', {} );

            const coverageInfo = get( response, 'body.component.structure.sections.coverageInfo', {} );
            const mailOrderCoverageInfo = get( response, 'body.component.structure.sections.mailOrderCoverageInfo', {} );
            const accumulatorInfo = get( response, 'body.component.structure.sections.accumulatorInfo', {} );

            const epostBasicInfo = get(
				response,
				'body.component.structure.sections.epostBasicInfo',
				{}
			);


            // get the option list
            const tabOptions = options[ get( response, 'body.component.structure.tabs.options', undefined ) ];
            const primaryColumns = get( response, 'body.component.structure.primaryColumns', {} );
            const defaultTabOptions  = [
                {"name":"Eligibility","title":{"refId":"","value":"Eligibility"},
                "value":{name: "Eligibility"}},
                {"name":"Mail Orders","title":{"refId":"","value":"Mail Order"},
                "value":{name: "Mail Orders"}}
            ]

            const _data = {};

            forEach( memberInfo, info => {
                if( ! isEmpty( get( info, 'coverageInfo', {} ) ) ) {
                    _data.coverageInfo = {
                        title: coverageInfo.title,
                        values: info.coverageInfo,
                        fields: getVisibleItems( coverageInfo.fields, visibility ),
                    };
                }

                if( ! isEmpty( get( info, 'mailOrderCoverageInfo', {} ) ) ) {
                    _data.mailOrderCoverageInfo = {
                        title: mailOrderCoverageInfo.title,
                        values: info.mailOrderCoverageInfo,
                        fields: getVisibleItems( mailOrderCoverageInfo.fields, visibility ),
                    };
                }

                if( get( info, 'accumulatorInfo.showAccumulatorData', false ) ) {
                    _data.accumulatorInfo = {
                        title: null,
                        showAccumulatorData: info.accumulatorInfo.showAccumulatorData,
                        fields: getVisibleItems( accumulatorInfo, visibility ),
                    };
                    
                }
                _data.coverage = info.coverage;
            } );
            const availableTabs = defaultTabOptions.filter( (tab, index) =>
                Boolean( index === 0 ? _data.coverageInfo : _data.mailOrderCoverageInfo )
            );
            const tabsConfig = {
                options: availableTabs,
                selectedTab: availableTabs.length ? availableTabs[0].value : null,
            };

            let tableResponse = {};
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    title,
                    basicInfo: {
                        title: basicInfo.title,
                        values: state.basicInfo,
                        fields: getVisibleItems( basicInfo.fields, visibility ),
                    },
                    epostBasicInfo: {
						title: epostBasicInfo.title,
						values: state.epostBasicInfo,
                        fields: getVisibleItems( basicInfo.fields, visibility ),

					},
                    memberInfo: _data,
                    options,
                    tabsConfig,
                    tableResponse,
                    actions
                },
            };
        } catch( error ) {

            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    async fetchAccumulatorInfo( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                    }
                } ),
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    ...get( response, 'body.component.data.state.accumulatorInfo', {} )
                },
            };
        } catch( error ) {

            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    async fetchMemberDataOnCovergaChange( { url={}, data={} } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                    }
                } ),
            } );
            
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    ...get( response, 'body.component.data.state', {} )
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }
}
